import { useMemo } from 'react';
import { FilterField } from '../../Common/DataList';
import { getTagForChangeState, getTagForRuntimeStates } from '../../Common/Tags';
import { changeStateOptions, runtimeStateOptions } from './constants';
import { ServiceItemsListConfig } from './types';

/**
 * The filter schema for the service items list. Shared so every layout variant
 * offers exactly the same filters, wherever it chooses to put them.
 */
const useServiceItemFilterFields = (config: ServiceItemsListConfig): FilterField[] => useMemo(() => [
    {
        // the number is what someone quotes when they are after one item; the
        // API matches it exactly, or a comma-separated few
        name: 'id',
        label: 'ID',
        type: 'search',
        placeholder: 'Search by ID',
        dataCy: 'id-search',
    },
    { name: 'name', label: 'Name', type: 'search', dataCy: 'name-search' },
    {
        // parent of the cascade, so it comes first
        name: 'consumer_team_id',
        label: 'Consumer Team',
        type: 'select',
        resource: config.teamsResource,
        dataCy: 'team-selector',
        hidden: !config.teamsResource,
    },
    {
        name: 'application_id',
        label: 'Application',
        type: 'select',
        resource: config.applicationsResource,
        dataCy: 'application-selector',
        // Applications are scoped to the chosen consumer team: disabled until
        // one is picked, refetched and cleared when it changes. Consumers have
        // no team choice, so there is nothing to wait for.
        dependsOn: config.teamsResource ? ['consumer_team_id'] : undefined,
    },
    {
        name: 'service_id',
        label: 'Service',
        type: 'select',
        resource: config.servicesResource,
        dataCy: 'service-selector',
    },
    {
        name: 'runtime_state',
        label: 'Runtime State',
        type: 'select',
        multiple: true,
        options: runtimeStateOptions,
        // dropdown rows use the same tag the rows and cards use
        optionRender: (option) => getTagForRuntimeStates(String(option.value)),
        dataCy: 'runtime-state-filter',
    },
    {
        name: 'change_state',
        label: 'Change State',
        type: 'select',
        multiple: true,
        options: changeStateOptions,
        optionRender: (option) => getTagForChangeState(String(option.value)),
        dataCy: 'change-state-filter',
    },
], [config.applicationsResource, config.servicesResource, config.teamsResource]);

export default useServiceItemFilterFields;
